/**
 * Click feedback helpers
 *
 * Pure functions for describing clicked elements with rich structural context.
 * Used by browser_click when click feedback is enabled via knob.
 *
 * Philosophy: Show data, don't judge. The semantic diff already tells the agent
 * what changed on the page. Click feedback describes what was clicked with full
 * context, letting the agent decide if that matches their intent.
 *
 * @module click-validation
 * @see docs/click-accuracy.md
 */

/**
 * Information about the element that was actually clicked.
 * Captured via `document.elementFromPoint()` after a click.
 */
export interface ClickedElementInfo {
  // Basic element info
  tagName: string;
  id?: string;
  className?: string;
  textContent?: string; // Up to 100 chars
  role?: string;
  ariaLabel?: string;

  // Parent context
  parentRole?: string; // grid, list, listbox, dialog, form, menu, etc.
  parentName?: string; // Accessible name of parent

  // Table/list context
  rowIndex?: number; // aria-rowindex or computed row position
  itemIndex?: number; // data-item-index for list items
  columnHeader?: string; // For gridcell: which column this is in
  rowText?: string; // Concatenated text of sibling cells in row

  // Additional identifiers
  dataTestId?: string; // data-testid or data-test-id
  labelText?: string; // Associated <label> text or aria-labelledby
  placeholder?: string; // Input placeholder
  altText?: string; // Image alt text
  title?: string; // title attribute

}

/**
 * Result returned from click() with element inspection.
 */
export interface ClickResult {
  clickedElement?: ClickedElementInfo;
  fallbackUsed: boolean;
}

/**
 * Format clicked element as a descriptive block for the agent.
 * No judgment (match/mismatch) - just describes what was clicked.
 * Returns null if insufficient info - caller should log warning and skip output.
 */
export function formatClickedElement(
  actual: ClickedElementInfo | undefined,
  fallbackUsed: boolean
): string | null {
  // No element captured - nothing useful to show
  if (!actual) {
    return null;
  }

  // Check if we have enough meaningful info
  const hasText = !!(
    actual.textContent ||
    actual.ariaLabel ||
    actual.labelText ||
    actual.altText
  );
  const hasContext = !!(actual.parentRole || actual.rowText || actual.columnHeader);

  // If we only have tagName with no identifying info, skip
  if (!hasText && !hasContext && !actual.role) {
    return null;
  }

  const lines: string[] = ['CLICKED:'];

  // Primary element description
  lines.push(`  ${formatPrimaryElement(actual)}`);

  // Context line (if in table/list/form)
  const context = formatContextLine(actual);
  if (context) {
    lines.push(`  Context: ${context}`);
  }

  // Note about fallback
  if (fallbackUsed) {
    lines.push('  NOTE: Coordinate fallback was used');
  }

  return lines.join('\n');
}

function formatPrimaryElement(el: ClickedElementInfo): string {
  const parts: string[] = [el.tagName];

  // Primary text identifier
  const text = el.textContent || el.ariaLabel || el.labelText || el.altText;
  if (text) {
    const truncated = text.length > 50 ? text.slice(0, 50) + '...' : text;
    parts.push(`"${truncated}"`);
  }

  // Role if informative (skip common ones that don't add info)
  if (el.role && !['button', 'link'].includes(el.role)) {
    parts.push(`(role=${el.role})`);
  }

  // Row context for table elements
  if (el.rowText) {
    const truncated = el.rowText.length > 60 ? el.rowText.slice(0, 60) + '...' : el.rowText;
    parts.push(`in row: "${truncated}"`);
  }

  // Index for list items
  if (el.itemIndex !== undefined) {
    parts.push(`(item ${el.itemIndex})`);
  }

  return parts.join(' ');
}

function formatContextLine(el: ClickedElementInfo): string | undefined {
  const parts: string[] = [];

  if (el.parentRole) {
    const parentDesc = el.parentName ? `${el.parentRole} "${el.parentName}"` : el.parentRole;
    parts.push(parentDesc);
  }

  if (el.rowIndex !== undefined) {
    parts.push(`row ${el.rowIndex}`);
  }

  if (el.columnHeader) {
    parts.push(`column "${el.columnHeader}"`);
  }

  return parts.length > 0 ? parts.join(' → ') : undefined;
}
